import { useState, useCallback, useEffect } from 'react';
import { User } from '@/models/UserTypes';
import { User as FirebaseUser } from 'firebase/auth';
import { mockUser as richMockUser } from '@/utils/mockTestData';

const DEV_STUDENT_VIEW_KEY = 'dev_student_view';

interface DevStudentViewState {
  isActive: boolean;
  userData: User | null;
  mockFirebaseUser: FirebaseUser | null;
}

// Use the comprehensive mock user from mockTestData.ts
export const mockUser: User = richMockUser;

// Create a mock Firebase user that integrates with auth
const createMockFirebaseUser = (): FirebaseUser => ({
  uid: 'mock-student-uid-12345',
  email: 'student.demo@example.com',
  displayName: 'Alex Student',
  emailVerified: true,
  isAnonymous: false,
  providerId: 'password', // Add missing providerId
  metadata: {
    creationTime: '2024-01-01T00:00:00.000Z',
    lastSignInTime: new Date().toISOString(),
  },
  providerData: [{
    providerId: 'password',
    uid: 'student.demo@example.com',
    displayName: 'Alex Student',
    email: 'student.demo@example.com',
    phoneNumber: null,
    photoURL: null
  }],
  refreshToken: 'mock-refresh-token',
  tenantId: null,
  phoneNumber: null,
  photoURL: null,
  // Add missing required methods as safe mocks
  getIdToken: async () => 'mock-id-token',
  getIdTokenResult: async () => ({
    token: 'mock-id-token',
    authTime: new Date().toISOString(),
    issuedAtTime: new Date().toISOString(),
    expirationTime: new Date(Date.now() + 3600000).toISOString(),
    signInProvider: 'password',
    signInSecondFactor: null,
    claims: { email: 'student.demo@example.com', email_verified: true }
  }),
  reload: async () => void 0,
  toJSON: () => ({
    uid: 'mock-student-uid-12345',
    email: 'student.demo@example.com',
    emailVerified: true,
    isAnonymous: false
  }),
  delete: async () => { throw new Error('Cannot delete mock user in dev mode'); }
} as FirebaseUser);

export const useDevStudentView = () => {
  const [devState, setDevState] = useState<DevStudentViewState>(() => {
    // Initialize from localStorage if available
    try {
      const stored = localStorage.getItem(DEV_STUDENT_VIEW_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          isActive: parsed.isActive || false,
          userData: parsed.isActive ? mockUser : null,
          mockFirebaseUser: parsed.isActive ? createMockFirebaseUser() : null
        };
      }
    } catch (error) {
      console.warn('Failed to parse dev student view from localStorage:', error);
    }
    return {
      isActive: false,
      userData: null,
      mockFirebaseUser: null
    };
  });

  // Persist to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem(DEV_STUDENT_VIEW_KEY, JSON.stringify({
        isActive: devState.isActive
      }));
    } catch (error) {
      console.warn('Failed to save dev student view to localStorage:', error);
    }
  }, [devState.isActive]);

  const activateStudentView = useCallback((user: User = mockUser) => {
    console.log('🚧 DEV: Activating Student View with integrated mock Firebase user');
    setDevState({
      isActive: true,
      userData: user,
      mockFirebaseUser: createMockFirebaseUser()
    });
  }, []);

  const deactivateStudentView = useCallback(() => {
    console.log('🚧 DEV: Deactivating Student View');
    setDevState({
      isActive: false,
      userData: null,
      mockFirebaseUser: null
    });
  }, []);

  const isDevelopment = (process.env.NODE_ENV === 'development' || 
                       window.location.hostname === 'localhost') &&
                       !window.location.hostname.includes('vercel') &&
                       !window.location.hostname.includes('netlify') &&
                       !window.location.hostname.includes('socialworktestprepacademy.com');

  return {
    isDevMode: isDevelopment,
    isStudentViewActive: devState.isActive,
    devUserData: devState.userData,
    mockFirebaseUser: devState.mockFirebaseUser,
    activateStudentView,
    deactivateStudentView
  };
};

export default useDevStudentView;